var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, memo, useReducer, useState, } from 'react';
import { Field } from 'react-final-form';
import { isValid, toDate, format } from 'date-fns';
import { P, Flex } from '@tpr/core';
import isEqual from 'lodash.isequal';
import { StyledInputLabel, InputElementHeading } from '../elements';
import { Input } from '../input/input';
import { SameMonthDateValidator } from './services/SameMonthDateValidator';
import AccessibilityHelper from '../accessibilityHelper';
import { HiddenLabelIdGenerator } from './services/HiddenLabelIdGenerator';
import styles from './date.module.scss';
var handleChange = function (onChange, value) { return function (_a) {
    var target = _a.target;
    var newValue = target.value.replace(/[^0-9]/g, '').trim();
    if (!newValue)
        return onChange('');
    if (parseInt(newValue, 10) < value) {
        return onChange(newValue);
    }
}; };
var sameMonthValidator = new SameMonthDateValidator();
function getValidDate(yyyy, mm, dd) {
    var date = toDate(new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd)));
    if (isValid(date) &&
        yyyy.length === 4 &&
        sameMonthValidator.ResolvedDateIsInSameMonth(yyyy, mm, dd)) {
        return format(date, 'yyyy-MM-dd');
    }
    return undefined;
}
var transformDate = function (initialDate) {
    if (!initialDate)
        return { dd: '', mm: '', yyyy: '' };
    var valid = initialDate ? isValid(initialDate) : false;
    return {
        dd: valid ? "" + initialDate.getDate() : '',
        mm: valid ? "" + (initialDate.getMonth() + 1) : '',
        yyyy: valid ? "" + initialDate.getFullYear() : '',
    };
};
var DateInputField = function (_a) {
    var disabled = _a.disabled, _b = _a.required, required = _b === void 0 ? false : _b, id = _a.id, label = _a.label, maxInt = _a.maxInt, maxLength = _a.maxLength, meta = _a.meta, onFocus = _a.onFocus, onBlur = _a.onBlur, parentId = _a.parentId, readOnly = _a.readOnly, _c = _a.small, small = _c === void 0 ? true : _c, testId = _a.testId, updateFn = _a.updateFn, value = _a.value, name = _a.name;
    var _d = useState(false), hasFocus = _d[0], setHasFocus = _d[1];
    var helper = new AccessibilityHelper(parentId, false, false);
    return (React.createElement("label", { className: small ? styles.inputSmall : styles.inputLarge },
        React.createElement(P, { cfg: {
                fontSize: 2,
                fontWeight: 4,
                mb: 1,
                lineHeight: 3,
                color: 'neutral.8',
            } }, label),
        React.createElement(Input, { type: "number", id: id, disabled: disabled, required: required, "data-testid": testId, value: value, readOnly: readOnly, onFocus: function (_a) {
                var target = _a.target;
                target.select();
                setHasFocus(true);
                onFocus && onFocus();
            }, onChange: handleChange(updateFn, maxInt), onBlur: function () {
                setHasFocus(false);
                onBlur && onBlur();
            }, meta: meta, maxLength: maxLength, isError: meta && meta.touched && meta.error, accessibilityHelper: !hasFocus ? helper : null, name: name })));
};
export var InputDate = memo(function (_a) {
    var id = _a.id, label = _a.label, hint = _a.hint, required = _a.required, input = _a.input, meta = _a.meta, _b = _a.testId, testId = _b === void 0 ? 'field' : _b, cfg = _a.cfg, disabled = _a.disabled, readOnly = _a.readOnly, hideDay = _a.hideDay, hideMonth = _a.hideMonth, _c = _a.hiddenLabel, hiddenLabel = _c === void 0 ? '' : _c;
    var _d = transformDate(meta.initial), dd = _d.dd, mm = _d.mm, yyyy = _d.yyyy;
    var _e = useReducer(function (p, n) { return (__assign(__assign({}, p), n)); }, { dd: dd, mm: mm, yyyy: yyyy }), _f = _e[0], day = _f.dd, month = _f.mm, year = _f.yyyy, setState = _e[1];
    useEffect(function () {
        setState({ dd: hideDay ? 1 : dd, mm: hideMonth ? 1 : mm, yyyy: yyyy });
    }, [dd, mm, yyyy]);
    useEffect(function () {
        if (input && typeof input.onChange === 'function') {
            var newDate = getValidDate(year, month, day);
            if (newDate) {
                input.onChange(newDate);
            }
            else {
                input.onChange(undefined);
            }
        }
    }, [day, month, year, input]);
    var hasValue = function () {
        return ((!hideDay && day !== '') || (!hideMonth && month !== '') || year !== '');
    };
    var isError = meta && meta.touched && meta.error;
    var hiddenLabelId = HiddenLabelIdGenerator(hiddenLabel);
    var helper = new AccessibilityHelper(id, !!label, !!hint, hiddenLabelId);
    return (React.createElement(StyledInputLabel, { id: id, isError: meta && meta.touched && meta.error, element: "fieldset", onBlur: function (e) {
            if ((required || hasValue()) &&
                e.relatedTarget &&
                !e.currentTarget.outerHTML.includes(e.relatedTarget.outerHTML)) {
                input.onBlur();
            }
        }, "data-testid": "date-input-" + testId, "aria-labelledby": helper.labelId, "aria-describedby": isError
            ? helper.formatAriaDescribedBy(isError)
            : !label && hiddenLabel !== ''
                ? hiddenLabelId
                : hint
                    ? helper.hintId
                    : helper.labelId, cfg: Object.assign({ mt: 1, py: 1 }, cfg), hiddenLabel: hiddenLabel, hiddenLabelId: hiddenLabelId },
        React.createElement(InputElementHeading, { element: "legend", label: label, required: required, hint: hint, meta: meta, accessibilityHelper: helper, errorRole: "alert" }),
        React.createElement(Flex, null,
            !hideDay && (React.createElement(DateInputField, { id: "dd-" + id, parentId: id, label: "Day", testId: "dd-" + testId, value: day, updateFn: function (dd) { return setState({ dd: dd }); }, maxInt: 32, meta: meta, disabled: disabled, readOnly: readOnly, required: required, maxLength: 2, name: input.name })),
            !hideMonth && (React.createElement(DateInputField, { id: "mm-" + id, parentId: id, label: "Month", testId: "mm-" + testId, value: month, updateFn: function (mm) { return setState({ mm: mm }); }, maxInt: 13, meta: meta, disabled: disabled, readOnly: readOnly, required: required, maxLength: 2, name: input.name })),
            React.createElement(DateInputField, { id: "yyyy-" + id, parentId: id, label: "Year", small: false, testId: "yyyy-" + testId, value: year, updateFn: function (yyyy) { return setState({ yyyy: yyyy }); }, maxInt: 10000, meta: meta, disabled: disabled, readOnly: readOnly, required: required, maxLength: 4, name: input.name }))));
}, function (_a, _b) {
    var prevMeta = _a.meta;
    var nextMeta = _b.meta;
    return isEqual(prevMeta, nextMeta);
});
export var FFInputDate = function (_a) {
    var type = _a.type, fieldProps = __rest(_a, ["type"]);
    return React.createElement(Field, __assign({}, fieldProps, { type: "text", component: InputDate }));
};
//# sourceMappingURL=date.js.map